/**
 * SplitBillingDriftTile — Phase 3F.5 (dev mode)
 * ────────────────────────────────────────────────────────────────────
 * Tile for the Activation Command Center that surfaces the current
 * split-billing reconciliation drift counts. Calls the Phase 3F.4
 * endpoints in parallel:
 *
 *   GET /api/admin/dev/split-billing-reconcile/status  → scheduler state
 *   GET /api/admin/dev/split-billing-drift?is_balanced=false → drift rows
 *
 * Renders:
 *   • CRITICAL / WARN / INFO counts
 *   • Scheduler tick freshness ("Last checked 3 min ago · interval 15m")
 *   • A "Refresh now" button that POSTs to reconcile-all
 *   • Up to 3 sample batch_ids with worst severity
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
    return `${Math.round(ms / 3_600_000)} hr ago`;
  } catch {
    return '—';
  }
}

export function SplitBillingDriftTile() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [state, setState] = useState({ status: null, drift: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, driftRes] = await Promise.all([
        api.get('/admin/dev/split-billing-reconcile/status').catch(() => null),
        api.get('/admin/dev/split-billing-drift', { params: { is_balanced: false } }).catch(() => null),
      ]);
      setState({
        status: statusRes?.data?.data || null,
        drift: driftRes?.data?.data || null,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshNow = async () => {
    setRunning(true);
    try {
      await api.post('/admin/dev/split-billing-reconcile');
      await load();
    } finally {
      setRunning(false);
    }
  };

  const driftRows = state.drift?.rows || [];
  const critical = driftRows.filter((r) => r.worst_severity === 'CRITICAL');
  const warn = driftRows.filter((r) => r.worst_severity === 'WARN');
  const info = driftRows.filter((r) => r.worst_severity === 'INFO');
  const summary = state.status?.last_tick_summary || {};
  const flagOff = summary?.code === 'RECON_FLAG_OFF';
  const totalBalanced = summary?.balanced ?? 0;
  const totalBatches = summary?.total_batches ?? 0;

  const headerIcon = critical.length > 0
    ? <ShieldAlert className="h-4 w-4 text-red-600" />
    : warn.length > 0
      ? <AlertTriangle className="h-4 w-4 text-amber-600" />
      : <ShieldCheck className="h-4 w-4 text-emerald-600" />;

  return (
    <Card data-testid="split-billing-drift-tile">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            {headerIcon}
            Split-Billing Drift (Phase 3F)
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            disabled={running || loading}
            onClick={refreshNow}
            data-testid="split-billing-drift-refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 ${running ? 'animate-spin' : ''}`} />
            Refresh now
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {flagOff && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
            Master flag <code>multi_service_real_billing_enabled</code> is OFF — reconciler is in safe no-op mode.
          </div>
        )}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
            <div className="text-lg font-bold text-emerald-700" data-testid="split-drift-balanced">{totalBalanced}</div>
            <div className="text-[10px] text-emerald-700 uppercase tracking-wide">Balanced</div>
          </div>
          <div className="rounded-md border border-sky-200 bg-sky-50 p-2">
            <div className="text-lg font-bold text-sky-700" data-testid="split-drift-info">{info.length}</div>
            <div className="text-[10px] text-sky-700 uppercase tracking-wide">Info</div>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
            <div className="text-lg font-bold text-amber-700" data-testid="split-drift-warn">{warn.length}</div>
            <div className="text-[10px] text-amber-700 uppercase tracking-wide">Warn</div>
          </div>
          <div className="rounded-md border border-red-200 bg-red-50 p-2">
            <div className="text-lg font-bold text-red-700" data-testid="split-drift-critical">{critical.length}</div>
            <div className="text-[10px] text-red-700 uppercase tracking-wide">Critical</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Last checked {relativeTime(state.status?.last_tick_at)} · scheduler {state.status?.running ? 'running' : 'stopped'}
            {state.status?.interval_seconds ? ` · every ${Math.round(state.status.interval_seconds / 60)} min` : ''}
          </span>
          <span>Total batches: {totalBatches}</span>
        </div>
        {critical.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs">
            <div className="mb-1 font-semibold text-red-800">CRITICAL drift sample:</div>
            <ul className="space-y-0.5 font-mono text-[11px] text-red-700">
              {critical.slice(0, 3).map((r) => (
                <li key={r.batch_id} data-testid={`split-drift-critical-batch-${r.batch_id}`}>
                  {r.batch_id} · {r.finding_count} finding(s)
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SplitBillingDriftTile;
